/**
 * The status filter row (M23-T03), drawn on six frames: a label, a count in a
 * pill, an underline on the active one.
 *
 * Counts are the caller's to supply and **must be real**. The frames' do not
 * add up — `All 5` sits over `3 + 1 + 1 + 2 + 0` (M23 defect D11) — so a page
 * that cannot count a bucket passes `undefined` and shows no pill rather than a
 * number it made up.
 *
 * Rendered as a tablist so the arrow keys behave the way the row looks.
 */
export type FilterTab<K extends string | null> = {
  key: K
  label: string
  count?: number
}

export function FilterTabs<K extends string | null>({
  tabs,
  active,
  onChange,
  label = 'Filter by status',
}: {
  tabs: readonly FilterTab<K>[]
  active: K
  onChange: (key: K) => void
  label?: string
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className="flex flex-wrap items-center gap-1 border-b border-line-1"
    >
      {tabs.map((tab) => {
        const isActive = tab.key === active
        return (
          <button
            key={tab.key ?? '__all'}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(tab.key)}
            className={[
              'flex items-center gap-1.5 border-b-2 px-3 pb-2.5 pt-1 text-sm',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
              isActive
                ? // The underline is the primary signal and font-semibold is the
                  // second, so the active tab is not carried by colour alone.
                  'border-accent font-semibold text-ink-1'
                : 'border-transparent font-medium text-ink-3 hover:text-ink-1',
            ].join(' ')}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className="rounded-full bg-bg-sunken px-1.5 py-0.5 text-xs tabular-nums text-ink-2">
                {tab.count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
